import React, { useEffect, useState } from 'react';
import { useTheme, Theme } from '@mui/material/styles';
import { createUseStyles } from 'react-jss';
import { Button, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import lmsIntegrationAgent from '../../services/api/lmsIntegrationAgent';
import { ELMSType, IIntegrationItem } from '../../services/models/LMSLinkModel';
import SnackbarUtils from '../../components/snackbarUtils/SnackbarUtilsConfigurator';

interface IntegrationComponentProps {
  ownerID: number;
  loadingOwnerID: boolean;
}

export function WPLogoIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg' fill='currentColor' {...props}>
      <path d='M61.7 169.4l101.5 278C92.2 413 43.3 340.2 43.3 256c0-30.9 6.6-60.1 18.4-86.6zm337.9 75.9c0-26.3-9.4-44.5-17.5-58.7-10.8-17.5-20.9-32.4-20.9-49.9 0-19.6 14.8-37.8 35.7-37.8.9 0 1.8.1 2.8.2-37.9-34.7-88.3-55.9-143.7-55.9-74.3 0-139.7 38.1-177.8 95.9 5 .2 9.7.3 13.7.3 22.2 0 56.7-2.7 56.7-2.7 11.5-.7 12.8 16.2 1.4 17.5 0 0-11.5 1.3-24.3 2l77.5 230.4L249.8 247l-33.1-90.8c-11.5-.7-22.3-2-22.3-2-11.5-.7-10.1-18.2 1.3-17.5 0 0 35.1 2.7 56 2.7 22.2 0 56.7-2.7 56.7-2.7 11.5-.7 12.8 16.2 1.4 17.5 0 0-11.5 1.3-24.3 2l76.9 228.7 21.2-70.9c9-29.4 16-50.5 16-68.7zm-139.9 29.3l-63.8 185.5c19.1 5.6 39.2 8.7 60.1 8.7 24.8 0 48.5-4.3 70.6-12.1-.6-.9-1.1-1.9-1.5-2.9l-65.4-179.2zm183-120.7c.9 6.8 1.4 14 1.4 21.9 0 21.6-4 45.8-16.2 76.2l-65 187.9C426.2 403 468.7 334.5 468.7 256c0-37-9.4-71.8-26-102.1zM504 256c0 136.8-111.3 248-248 248C119.2 504 8 392.7 8 256 8 119.2 119.2 8 256 8c136.7 0 248 111.2 248 248zm-11.4 0c0-130.5-106.2-236.6-236.6-236.6C125.5 19.4 19.4 125.5 19.4 256S125.6 492.6 256 492.6c130.5 0 236.6-106.1 236.6-236.6z' />
    </svg>
  );
}

export function NiasLogoIcon(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  return <img src='/img/logoNIAS.svg' alt='norsk interaktiv logo' height={'100%'} {...props} />;
}

export function SynthesiaLogoIcon(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  return <img src='/img/brand/external/synthesia-small.png' alt='synthesia logo' {...props} />;
}

export default function ActiveIntegrations({ ownerID, loadingOwnerID: _loadingOwnerID }: IntegrationComponentProps) {
  const theme = useTheme();

  const [integrations, setIntegrations] = useState<IIntegrationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch integrations when component mounts
  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setLoading(true);
        setError(null);
        const response: any = await lmsIntegrationAgent.WPLink.getWPLinks(ownerID);
        const items: any[] = response && response.items ? response.items : response;
        const mapped: IIntegrationItem[] = (items || []).map((item: any) => ({
          customerId: item?.lmsCustomerId ?? item?.customerId,
          url: item?.lmsCustomer ?? item?.url,
          lmsTypeId: item?.lmsTypeId,
          ownerId: item?.ownerId,
        }));

        setIntegrations(mapped);
      } catch (error: any) {
        console.error('Error fetching integrations:', error);
        setError('Failed to load integrations');
        SnackbarUtils.error('Failed to load integrations');
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, [ownerID]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState<IIntegrationItem | null>(null);

  const handleDeleteClick = (integration: IIntegrationItem) => {
    setIntegrationToDelete(integration);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!integrationToDelete) return;

    setDeleting(true);
    try {
      await lmsIntegrationAgent.CourseLMSLink.deleteLMSLink(integrationToDelete.customerId, ownerID);

      // Remove from local state on successful deletion
      setIntegrations((prev) => prev.filter((integration) => integration.customerId !== integrationToDelete.customerId));

      SnackbarUtils.success('Integration deleted successfully');
    } catch (error: any) {
      console.error('Error deleting integration:', error);
      SnackbarUtils.error('Failed to delete integration');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setIntegrationToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setIntegrationToDelete(null);
  };

  const useStyles = createUseStyles((theme: Theme) => ({
    integrationItem: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.border.main}`,
      borderRadius: theme.spacing(1),
      marginBottom: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 48,
      height: 48,
      marginRight: theme.spacing(2),
      '& svg': {
        width: 32,
        height: 32,
      },
      '& img': {
        height: 32,
        width: 'auto',
        maxHeight: 32,
      },
    },
    integrationInfo: {
      flex: 1,
    },
    integrationUrl: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
    },
    integrationType: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      textTransform: 'capitalize',
    },
    actionButtons: {
      marginLeft: 'auto',
      display: 'flex',
      gap: theme.spacing(1),
    },
    goToSiteButton: {
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.light + '20',
      },
    },
    deleteButton: {
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: theme.palette.error.light + '20',
      },
    },
  }));

  const classes = useStyles({ theme });

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', flexDirection: 'column', gap: theme.spacing(4) }}>
      <div style={{ overflow: 'auto', background: theme.palette.bgTwo.main, display: 'flex', width: '100%' }}>
        <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', padding: theme.spacing(2), gap: theme.spacing(2) }}>
          <div>
            <Typography variant='h6' gutterBottom>
              Active Integrations
            </Typography>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: theme.spacing(4) }}>
                <CircularProgress size={40} />
                <Typography variant='body1' style={{ marginLeft: theme.spacing(2) }}>
                  Loading integrations...
                </Typography>
              </div>
            ) : error ? (
              <Typography variant='body1' color='error'>
                {error}
              </Typography>
            ) : integrations.length > 0 ? (
              integrations.map((integration) => (
                <div key={integration.customerId} className={classes.integrationItem}>
                  <div className={classes.iconContainer}>
                    {integration.lmsTypeId === ELMSType.WordPress && <WPLogoIcon />}
                    {integration.lmsTypeId === ELMSType.Mentorkit_LMS && <NiasLogoIcon />}
                    {integration.lmsTypeId === ELMSType.Synthesia && <SynthesiaLogoIcon />}
                  </div>
                  <div className={classes.integrationInfo}>
                    <Typography className={classes.integrationUrl}>{integration.url}</Typography>
                    <Typography className={classes.integrationType}>{ELMSType[integration.lmsTypeId].replaceAll('_', ' ')}</Typography>
                  </div>
                  <div className={classes.actionButtons}>
                    {integration.lmsTypeId === ELMSType.WordPress && (
                      <IconButton
                        className={classes.goToSiteButton}
                        component='a'
                        href={`${integration.url}/wp-admin/admin.php?page=mentorkit`}
                        target='_blank'
                        rel='noopener noreferrer'
                        size='small'
                        title='Go to SCORM Player Uploader'
                      >
                        <LaunchIcon />
                      </IconButton>
                    )}

                    <IconButton className={classes.deleteButton} onClick={() => handleDeleteClick(integration)} size='small' title='Delete integration'>
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </div>
              ))
            ) : (
              <Typography variant='body1' color='textSecondary'>
                No active integrations found.
              </Typography>
            )}
          </div>
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onClose={deleting ? undefined : handleDeleteCancel}>
        <DialogTitle>Delete Integration</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete the integration with {integrationToDelete?.url}? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color='error' variant='contained' disabled={deleting}>
            {deleting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
